/** Hand-rolled windowing math for long tables (INP-778).
 *
 *  Pure — the hook (`useWindowedRows`) feeds it scroll geometry plus a
 *  per-row measured-height cache; the component renders
 *  `rows.slice(start, end)` between two spacer rows of `topPad` /
 *  `bottomPad` pixels so the DOM holds only the visible slice (plus
 *  overscan) while the scrollbar geometry stays identical to a
 *  fully-rendered list.
 *
 *  Heights are variable: measured rows use their real height; unmeasured
 *  rows use the average of the measured ones (or the caller's default
 *  before anything measured). Offsets are a prefix sum — O(rowCount) per
 *  rebuild, fine at the ≤200k rows these tables see.
 */

export interface RowWindowInput {
  /** Document scroll position (window.scrollY). */
  scrollTop: number;
  /** Viewport height (window.innerHeight). */
  viewportHeight: number;
  /** Distance from the document top to the first row (container offset). */
  listTop: number;
  /** Fixed per-row height in px. */
  rowHeight: number;
  rowCount: number;
  /** Extra rows rendered above/below the visible range. */
  overscan: number;
}

export interface RowWindow {
  /** First rendered row index (inclusive). */
  start: number;
  /** One past the last rendered row index (exclusive). */
  end: number;
  /** Spacer height above the rendered slice, px. */
  topPad: number;
  /** Spacer height below the rendered slice, px. */
  bottomPad: number;
}

export interface RowGeometry {
  /** offsets[i] = pixel top of row i; offsets[rowCount] = total height. */
  offsets: number[];
  /** Height used for rows without a measurement (average of measured
   *  rows, or the caller's default when nothing is measured yet). */
  estimate: number;
}

/** Prefix-sum the per-row heights into offsets. `heights` is sparse —
 *  indices without a measurement fall back to the average of measured
 *  rows so the estimate self-corrects as more rows render. */
export function buildRowGeometry(
  rowCount: number,
  heights: ReadonlyArray<number | undefined>,
  defaultEstimate: number,
): RowGeometry {
  let sum = 0;
  let measured = 0;
  for (let i = 0; i < rowCount; i++) {
    const h = heights[i];
    if (h !== undefined) {
      sum += h;
      measured += 1;
    }
  }
  const estimate = measured > 0 ? sum / measured : defaultEstimate;
  const offsets = new Array<number>(rowCount + 1);
  offsets[0] = 0;
  for (let i = 0; i < rowCount; i++) {
    offsets[i + 1] = offsets[i] + (heights[i] ?? estimate);
  }
  return { offsets, estimate };
}

export interface VariableRowWindowInput {
  scrollTop: number;
  viewportHeight: number;
  listTop: number;
  rowCount: number;
  overscan: number;
  geometry: RowGeometry;
  /** Row indices that must stay rendered (open inline forms) even when
   *  scrolled outside the window, so their component state survives. */
  pinned?: ReadonlySet<number>;
}

export interface WindowedRowPlan extends RowWindow {
  /** Pinned indices below `start`, ascending — render before the top
   *  spacer; their heights are already subtracted from `topPad`. */
  pinnedBefore: number[];
  /** Pinned indices at/after `end`, ascending — render after the bottom
   *  spacer; their heights are already subtracted from `bottomPad`. */
  pinnedAfter: number[];
}

/** Smallest i in [0, rowCount] whose bottom edge (offsets[i+1]) is past y. */
function firstIndexWithBottomPast(offsets: number[], rowCount: number, y: number): number {
  let lo = 0;
  let hi = rowCount;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (offsets[mid + 1] > y) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

/** Smallest i in [0, rowCount] whose top edge (offsets[i]) is at/past y. */
function firstIndexAtOrPast(offsets: number[], rowCount: number, y: number): number {
  let lo = 0;
  let hi = rowCount;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (offsets[mid] >= y) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

export function computeVariableRowWindow({
  scrollTop,
  viewportHeight,
  listTop,
  rowCount,
  overscan,
  geometry,
  pinned,
}: VariableRowWindowInput): WindowedRowPlan {
  if (rowCount <= 0) {
    return { start: 0, end: 0, topPad: 0, bottomPad: 0, pinnedBefore: [], pinnedAfter: [] };
  }
  const { offsets, estimate } = geometry;
  const total = offsets[rowCount];
  // Visible band of the list in list-local coordinates. Fully scrolled
  // past (or before) the list degenerates to an empty slice at the
  // nearest edge, with the pads still summing to the full list height so
  // the scrollbar doesn't jump. Beyond either edge the raw index
  // extrapolates at the uniform estimate so overscan clamping matches
  // the fixed-height math exactly.
  const visibleTop = scrollTop - listTop;
  const visibleBottom = visibleTop + viewportHeight;
  const rawStart =
    visibleTop >= total
      ? rowCount + Math.floor((visibleTop - total) / estimate)
      : firstIndexWithBottomPast(offsets, rowCount, visibleTop);
  const rawEnd =
    visibleBottom <= 0
      ? Math.ceil(visibleBottom / estimate)
      : firstIndexAtOrPast(offsets, rowCount, visibleBottom);
  const start = Math.min(rowCount, Math.max(0, rawStart - overscan));
  const end = Math.min(rowCount, Math.max(start, rawEnd + overscan));
  let topPad = offsets[start];
  let bottomPad = total - offsets[end];
  const pinnedBefore: number[] = [];
  const pinnedAfter: number[] = [];
  if (pinned && pinned.size > 0) {
    for (const i of [...pinned].sort((a, b) => a - b)) {
      if (i < 0 || i >= rowCount) continue;
      if (i < start) {
        pinnedBefore.push(i);
        topPad -= offsets[i + 1] - offsets[i];
      } else if (i >= end) {
        pinnedAfter.push(i);
        bottomPad -= offsets[i + 1] - offsets[i];
      }
    }
    topPad = Math.max(0, topPad);
    bottomPad = Math.max(0, bottomPad);
  }
  return { start, end, topPad, bottomPad, pinnedBefore, pinnedAfter };
}

/** Fixed-height convenience wrapper: uniform geometry through the
 *  variable-height path, so both stay in lockstep by construction. */
export function computeRowWindow({
  scrollTop,
  viewportHeight,
  listTop,
  rowHeight,
  rowCount,
  overscan,
}: RowWindowInput): RowWindow {
  if (rowCount <= 0 || rowHeight <= 0) {
    return { start: 0, end: 0, topPad: 0, bottomPad: 0 };
  }
  const { start, end, topPad, bottomPad } = computeVariableRowWindow({
    scrollTop,
    viewportHeight,
    listTop,
    rowCount,
    overscan,
    geometry: buildRowGeometry(rowCount, [], rowHeight),
  });
  return { start, end, topPad, bottomPad };
}

/** Re-clamp a (possibly stale) window after `rowCount` shrank — e.g. a
 *  filter narrowed the list between scroll events. An untouched window
 *  passes through so variable-height pads survive; a clamped one falls
 *  back to estimate-based pads for the single frame until the hook's
 *  recompute lands exact geometry. */
export function clampRowWindow(w: RowWindow, rowCount: number, rowHeight: number): RowWindow {
  if (w.end <= rowCount) return w;
  const start = Math.min(w.start, rowCount);
  const end = Math.max(start, Math.min(w.end, rowCount));
  return {
    start,
    end,
    topPad: start * rowHeight,
    bottomPad: (rowCount - end) * rowHeight,
  };
}

/** Row count under which windowing is pure overhead — render everything.
 *  ~200 rows is well under a frame's budget and keeps Ctrl-F usable on
 *  small runs. */
export const WINDOWING_MIN_ROWS = 200;
